import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { MessageCircle, X, Send } from 'lucide-react';
import { useChatMessages } from './ChatBot/useChatMessages';
import { generateSessionId, quickQuestions, formatTimestamp } from './ChatBot/helpers';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_smile-automation-1/artifacts/ozqzxoju_Dentiflow%20Large%20Logo%20Plain.png';
const TOOLTIP_DELAY_MS = 20000; // 20 seconds

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sessionId] = useState(generateSessionId);
  const messagesEndRef = useRef(null);

  const { messages, isTyping, sendMessage } = useChatMessages(sessionId);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Show tooltip after delay - TOOLTIP_DELAY_MS is a constant, only isOpen is reactive
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setShowTooltip(true);
      }, TOOLTIP_DELAY_MS);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSendMessage = async (messageText = null) => {
    // If messageText is an event object or not a string, use inputValue
    const textToSend = (typeof messageText === 'string') ? messageText : inputValue;
    
    if (!textToSend || !textToSend.trim()) return; // Don't send empty messages
    
    setInputValue(''); // Clear input immediately
    await sendMessage(textToSend);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default form submission
      handleSendMessage();
    }
  };

  const handleQuickQuestion = (questionText) => {
    handleSendMessage(questionText);
    setShowPrompt(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence mode="wait">
        {isOpen ? (
          <motion.div
            key="chatbot-window"
            initial={{ opacity: 0, scale: 0.85, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 20 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <ChatWindow
              messages={messages}
              isTyping={isTyping}
              inputValue={inputValue}
              showPrompt={showPrompt}
              onClose={() => setIsOpen(false)}
              onInputChange={setInputValue}
              onKeyDown={handleKeyDown}
              onSendMessage={handleSendMessage}
              onQuickQuestion={handleQuickQuestion}
              messagesEndRef={messagesEndRef}
            />
          </motion.div>
        ) : (
          <motion.div
            key="chatbot-toggle"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <ChatToggleButton
              showTooltip={showTooltip}
              onOpen={() => {
                setIsOpen(true);
                setShowPrompt(false);
                setShowTooltip(false);
              }}
              onDismissTooltip={() => setShowTooltip(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Extracted ChatWindow component
const ChatWindow = ({
  messages,
  isTyping,
  inputValue,
  showPrompt,
  onClose,
  onInputChange,
  onKeyDown,
  onSendMessage,
  onQuickQuestion,
  messagesEndRef
}) => (
  <div className="w-[340px] sm:w-[380px] md:w-[420px]">
    <Card className="bg-[#1F2937] border-black shadow-2xl">
      <CardHeader className="border-b border-black bg-[#4FC3F7] py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src={LOGO_URL} alt="DentiFlow" className="h-8 sm:h-10 w-auto" />
            <CardTitle className="text-base sm:text-lg font-sans font-semibold text-black">
              DentiFlow AI Assistant
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-[#3AB3E7] rounded-full text-black w-8 h-8 sm:w-10 sm:h-10"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-3 sm:p-4 bg-[#0F1115]">
        <ScrollArea className="h-[350px] sm:h-[400px] pr-2 sm:pr-4">
          <MessageList messages={messages} />
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </ScrollArea>

        {showPrompt && (
          <QuickQuestions onQuickQuestion={onQuickQuestion} />
        )}

        <ChatInput
          value={inputValue}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onSend={onSendMessage}
        />
      </CardContent>
    </Card>
  </div>
);

// Extracted MessageList component
const MessageList = ({ messages }) => (
  <div className="space-y-4">
    {messages.map((message) => (
      <div
        key={message.id}
        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={`max-w-[80%] rounded-2xl px-4 py-3 ${
            message.sender === 'user'
              ? 'bg-[#4FC3F7] text-black'
              : 'bg-[#374151] text-white'
          }`}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
          <p className="text-xs mt-1 opacity-60">
            {formatTimestamp(message.timestamp)}
          </p>
        </div>
      </div>
    ))}
  </div>
);

// Extracted TypingIndicator component
const TypingIndicator = () => (
  <div className="flex justify-start mb-4">
    <div className="bg-[#374151] rounded-2xl px-4 py-3">
      <div className="flex gap-1">
        <div className="w-2 h-2 bg-[#4FC3F7] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-[#4FC3F7] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-[#4FC3F7] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

// Extracted QuickQuestions component
const QuickQuestions = ({ onQuickQuestion }) => (
  <div className="mb-4 p-3 bg-[#374151]/50 rounded-lg">
    <p className="text-xs text-gray-400 mb-2">Quick questions:</p>
    <div className="flex flex-wrap gap-2">
      {quickQuestions.map((q) => (
        <button
          key={q.id}
          onClick={() => onQuickQuestion(q.text)}
          className="text-xs px-3 py-1.5 bg-[#4FC3F7] hover:bg-[#3AB3E7] text-black rounded-full transition-colors"
        >
          {q.text}
        </button>
      ))}
    </div>
  </div>
);

// Extracted ChatInput component
const ChatInput = ({ value, onChange, onKeyDown, onSend }) => (
  <div className="flex gap-2 mt-4">
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type your message..."
      onKeyDown={onKeyDown}
      className="flex-grow bg-white transition-all focus:ring-2 focus:ring-[#4FC3F7]"
    />
    <Button
      size="icon"
      onClick={onSend}
      className="bg-[#4FC3F7] hover:bg-[#3AB3E7] hover:scale-105 transition-all duration-200"
    >
      <Send className="w-4 h-4 text-black" />
    </Button>
  </div>
);

// Extracted ChatToggleButton component
const ChatToggleButton = ({ showTooltip, onOpen, onDismissTooltip }) => (
  <div className="relative">
    {showTooltip && (
      <div 
        className="absolute bottom-full right-0 mb-3 sm:mb-4 bg-[#4FC3F7] text-black px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-lg whitespace-nowrap animate-bounce text-xs sm:text-sm"
        onClick={onDismissTooltip}
        onMouseEnter={onDismissTooltip}
      >
        <div className="font-semibold">Have a question?</div>
        <div className="absolute bottom-0 right-5 sm:right-6 transform translate-y-1/2 rotate-45 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#4FC3F7]"></div>
      </div>
    )}
    <Button
      onClick={onOpen}
      size="lg"
      className="rounded-xl w-14 h-14 sm:w-16 sm:h-16 bg-[#4FC3F7] hover:bg-[#3AB3E7] shadow-lg hover:shadow-2xl hover:scale-110 transition-all duration-300"
    >
      <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-black fill-black" />
    </Button>
  </div>
);

export default ChatBot;
